import React, { useState, useMemo } from 'react';
import { AppSettings } from '../state/types';
import { Trash2, RotateCcw, AlertTriangle } from 'lucide-react';

interface EditorProps {
  text: string;
  settings: AppSettings;
  onChange: (newText: string) => void;
  onClear: () => void;
  onUndoClear: () => void;
  canUndoClear: boolean;
}

export const Editor: React.FC<EditorProps> = ({
  text,
  settings,
  onChange,
  onClear,
  onUndoClear,
  canUndoClear,
}) => {
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // Compute word and character count
  const stats = useMemo(() => {
    const chars = text.length;
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    return { chars, words };
  }, [text]);

  const handleClearClick = () => {
    setShowClearConfirm(true);
  };

  const handleConfirmClear = () => {
    setShowClearConfirm(false);
    onClear();
  };

  const handleCancelClear = () => {
    setShowClearConfirm(false);
  };

  // Font family class
  const fontClass =
    settings.fontFamily === 'naskh'
      ? 'font-naskh'
      : settings.fontFamily === 'amiri'
      ? 'font-amiri'
      : 'font-sans';

  return (
    <div className="flex flex-col h-full w-full bg-neutral-950 border border-neutral-800 rounded-lg overflow-hidden shadow-xl">
      {/* Editor Header / Stats Bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-neutral-900 border-b border-neutral-800 text-xs text-neutral-400">
        <div className="flex items-center space-x-4">
          <span>{stats.words.toLocaleString()} words</span>
          <span>•</span>
          <span>{stats.chars.toLocaleString()} characters</span>
        </div>

        <div className="flex items-center space-x-2">
          {canUndoClear && (
            <button
              onClick={onUndoClear}
              className="flex items-center space-x-1 px-2.5 py-1 bg-neutral-800 hover:bg-neutral-700 text-neutral-200 rounded text-xs transition"
              title="Restore cleared script"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Undo Clear</span>
            </button>
          )}

          <button
            onClick={handleClearClick}
            disabled={!text}
            className="flex items-center space-x-1 px-2.5 py-1 text-red-400 hover:bg-red-950/40 rounded text-xs transition disabled:opacity-30 disabled:pointer-events-none"
            title="Clear entire script"
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span>Clear</span>
          </button>
        </div>
      </div>

      {/* Confirmation Modal for Clear */}
      {showClearConfirm && (
        <div className="bg-red-950/70 border-b border-red-800 px-4 py-3 flex items-center justify-between animate-fadeIn">
          <div className="flex items-center space-x-2 text-red-200 text-xs">
            <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
            <span>Clear entire script? You can undo immediately if needed.</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleConfirmClear}
              className="px-3 py-1 bg-red-600 hover:bg-red-500 text-white rounded text-xs font-medium"
            >
              Clear
            </button>
            <button
              onClick={handleCancelClear}
              className="px-3 py-1 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 rounded text-xs"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Native Textarea (§4: untouched code points, native IME & bidi) */}
      <textarea
        dir={settings.directionMode === 'auto' ? 'auto' : settings.directionMode}
        value={text}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="off"
        className={`w-full flex-1 p-6 bg-neutral-950 text-neutral-100 placeholder-neutral-600 resize-none outline-hidden focus:ring-0 leading-relaxed overflow-y-auto ${fontClass} arabic-script-surface`}
        style={{
          fontSize: '28px',
          lineHeight: 1.7,
        }}
        placeholder="ألصق النص العربي هنا للبدء بالبث المباشر..."
      />
    </div>
  );
};
